<template>
  <div id="tos-page" class="content">
    <h4>Terms of Service</h4>
    <p class="date">Last Updated: Feb 1, 2017</p>
    <ul class="legal-list">
      <li>
        <a href="/privacy">Privacy Policy</a>
      </li>
    </ul>
    <hr />
    <p>
      We are OpenLikes Tech, Inc. (
      <strong>“OpenLikes”</strong>, <strong>“us”</strong>, <strong>“we”</strong> or
      <strong>“our”</strong>), a corporation registered under the laws of the state of Delaware in
      the United States with its principal office address at 660 4th Street #558, San Francisco, CA
      94107, USA.
    </p>
    <hr />
    <h5>About the OpenLikes Services</h5>
    <p>
      We provide users (
      <strong>“you”</strong>, <strong>“your”</strong>) with services, delivered via the OpenLikes
      website (currently located at
      <a href="http://www.openlikes.com">http://www.openlikes.com</a>), the OpenLikes mobile
      applications, and/or any other platform or media through which we may make such services
      available from time to time, that allow you to explore the Internet, find new websites that
      may be of interest to you, receive recommendations of websites from OpenLikes, and join a
      community of individuals with similar tastes and interests (collectively, the
      <strong>“Services”</strong>).
    </p>
    <hr />
    <h5>About These Terms of Service</h5>
    <p>
      These OpenLikes Terms of Service (
      <strong>“Terms of Service”</strong>) set forth the legally binding terms for your use of the
      Services. You should also read the OpenLikes Privacy Policy, currently located
      <a href="/privacy">here</a>, and accessible through the OpenLikes mobile applications, which
      contains important information about how we use your data. By accessing and using the
      Services, you agree to comply with and be bound by these Terms of Service. If you do not agree
      to these Terms of Service, you may not access or use the Services.
    </p>
    <hr />
    <h5>Changes to These Terms of Service</h5>
    <p>
      OpenLikes reserves the right to change these Terms of Service from time to time. We will
      usually inform you of any changes by posting the updated Terms of Service on the OpenLikes
      website, which also may be accessed through the mobile applications. If we make any material
      changes to these Terms of Service, we will post a notification on the OpenLikes website and,
      if you have provided us with contact information, we will notify you via your preferred means
      of contact and/or by asking you to review the changes to these Terms of Service the next time
      you access the Services. Your continued use of the Services after the changes are communicated
      will indicate your acceptance of the Terms of Service, including those changes.
    </p>
    <hr />
    <h5>Other Services</h5>
    <p>
      If you sign up for additional services from OpenLikes, those additional services may be
      governed by terms associated with those services, which will apply to you in addition to these
      Terms of Service. If there is a conflict between these Terms of Service and terms and
      conditions for such additional services, the latter shall take precedence with respect to the
      additional services.
    </p>
    <hr />
    <h5>Creating an Account</h5>
    <p>
      You may choose to create a OpenLikes account or you may use the Services without creating an
      account. Either way, we consider you a
      <strong>“User”</strong> of the Services. You may also have created an account through one of
      OpenLikes’s connected social networking communities ( <strong>“Partner Communities”</strong>).
      Once you have created an account you may choose to add a photo or other information to your
      profile (the <strong>“OpenLikes Profile”</strong>).
    </p>
    <hr />
    <h5>Eligibility</h5>
    <p>By using the Services, you represent that:</p>
    <ol class="legal-list">
      <li>
        All required registration information you submit either as part of the OpenLikes
        registration process or by your registration through a Partner Community is truthful and
        accurate.
      </li>
      <li>You will maintain the accuracy of such information.</li>
      <li>
        If you are using the Services on behalf of a legal entity, you represent that you are
        authorized to enter into an agreement on behalf of that legal entity.
      </li>
      <li>You are 13 years of age or older.</li>
      <li>Your use of the Services does not violate any applicable law or regulation.</li>
    </ol>
    <p>
      Your OpenLikes account may be deleted if we have reason to believe that you do not meet these
      eligibility requirements.
    </p>
    <hr />
    <h5>Your Password</h5>
    <p>
      When you register, you will be asked to choose a password. You are responsible for maintaining
      the confidentiality of your password and all use of account. You agree not to use the account
      or password of another User. You agree to notify us immediately if you suspect any
      unauthorized use of your account or access to your password.
    </p>
    <hr />
    <h5>Allowed Used of Services</h5>
    <p>
      The Services are made available to you for your non-commercial use only. Other than permitted
      advertising, you may not use the Services to promote a product or service. OpenLikes makes no
      guarantee of traffic to any particular website or application. You may not defame, stalk,
      bully, abuse, harass, threaten, impersonate or intimidate others through the Services. You may
      not modify, reproduce, download, store, distribute, publish, license, or create derivative
      works from the Services. You may not use the Services in any way that could damage, disable or
      overburden the Services or the networks or services connected to the Services. You will not,
      and will not enable others to, crawl, scrape, cache or otherwise access any Content on the
      Service via automated means. You may not use any third-party APIs, software, tools or scripts
      to access or modify the Services or underlying technology, unless expressly authorized in
      writing by OpenLikes, or as permitted in any other agreement you enter into with OpenLikes.
      You agree that you are responsible for all data charges you incur through use of the Services.
      OpenLikes may restrict, deactivate or terminate your access to the Services or your account
      for any reason. OpenLikes reserves the right, at its sole discretion, to modify, discontinue,
      or terminate the Services at any time and without prior notice.
    </p>
    <hr />
    <h5>Ownership</h5>
    <p>
      You acknowledge that OpenLikes owns all right, title and interest in and to the Services,
      including all underlying software and technology and all Intellectual Property Rights in
      relation to the Services, other than those owned by third parties.
      <strong>“Intellectual Property Rights”</strong> means any and all intellectual property rights
      existing from time to time in any part of the world under applicable law, including patent
      law, copyright law, trade secret law, trademark law, unfair competition law. OpenLikes grants
      you the personal, non-exclusive, revocable, non-transferable right to use the software and
      technology that is provided to you for your use of the Services as permitted under these Terms
      of Service.
    </p>
    <hr />
    <h5>Content</h5>
    <p>
      The Services contain different types of Content: OpenLikes Content, Third Party Content and
      User Content.
      <strong>“Content”</strong> means any and all text, images, photos, videos, music, sounds,
      postings, links, messages, recommendations, comments, files, feedback, bug reports, or other
      materials and works of authorship. Each type of Content that you encounter has different rules
      and restrictions, as set forth below:
    </p>
    <h5>OpenLikes Content</h5>
    <p>
      As between OpenLikes and you, OpenLikes owns or controls all right, title and interest in the
      Services and in the Content that it has placed on and within the Services, including its
      logos, trademarks, page headers, custom graphics, button icons, scripts and all other
      Intellectual Property Rights it controls (
      <strong>“OpenLikes Content”</strong>).
    </p>
    <h5>Third-Party Content</h5>
    <p>
      Content from third parties, including advertisers, is displayed through the Services (
      <strong>“Third Party Content”</strong>). You may add or recommend such Third Party Content to
      the OpenLikes Services and you may share Third Party Content with others using the share
      feature. We do not filter or exercise control over Third Party Content and you agree that we
      are not responsible for any such Third Party Content, including advertising and/or information
      about products or services. We make no guarantees about the accuracy, currency, suitability,
      quality or legality of the information in such Third Party Content, and we assume no
      responsibility for unintended, objectionable, inaccurate, misleading or unlawful Third Party
      Content. By choosing your preferences, you can designate types and categories of Content you
      prefer to access, and you can report Content that you think is inappropriate, but OpenLikes
      cannot guarantee that you will only access the types and categories of Content you designate.
      OpenLikes is not responsible for Third Party Content that contains or delivers viruses, Trojan
      horses, worms, time bombs, cancelbots, corrupted files, spyware, malware, or other harmful
      content or code. Rather, you are responsible for installing, maintaining and using effective
      anti-virus and spyware prevention technologies.
    </p>
    <p>
      <strong>YouTube.</strong> Please note that YouTube services included in our products are
      covered by <a href="https://www.youtube.com/t/terms">YouTube's Terms of Service</a>.
    </p>
    <h5>User Content</h5>
    <p>
      We do not claim ownership in any Content that you add, post, display, or otherwise make
      available to the Services through your OpenLikes account, your comments, or other features of
      the Services that may allow you to post your own Content (
      <strong>“User Content”</strong>). By posting User Content, you hereby grant to us an
      unrestricted, irrevocable, perpetual, non-exclusive, fully-paid and royalty-free license (with
      the right to sublicense through unlimited levels of sublicensees) to use, copy, perform,
      display, create derivative works of, and distribute your User Content in any and all media or
      distribution method (now known or later developed) throughout the world. The licenses granted
      here will survive the termination or expiration of these Terms of Service. You are solely
      responsible for any and all User Content that you post on the Services and for your
      interactions with other Users. You should only post User Content to the Services that you own
      and that you are comfortable sharing with others. You may not post a photograph or video of
      another person without that person’s permission. You represent that: (1) you own the User
      Content posted by you on the Service or otherwise have the right to grant the licenses set
      forth here, and (2) your User Content and the use of it by OpenLikes does not violate the
      privacy rights, publicity rights, Intellectual Property Rights, or other rights of any person.
    </p>
    <h5>Content Restrictions</h5>
    <p>
      Except for that Content that is owned by you, that is in the public domain, for which you have
      been given written permission, or as expressly permitted under these Terms of Service, you
      agree not to: (1) modify, copy, publish, display, transmit, distribute, perform, sell or
      create derivative works based on the Content and Services; (2) rent, lease, loan or sell
      access to the Services; or (3) decompile, reverse engineer or copy any Content or the
      Services. You also agree to not remove, obscure or alter OpenLikes’s or a third party’s
      copyright notice, logos, trademarks or other proprietary rights notices affixed to or
      contained within the Services. You may not remove any advertising or branding presented within
      the Services.
    </p>
    <h5>Prohibited Content</h5>
    <p>
      You agree that you will not post or share through the Services any Prohibited Content.
      <strong>“Prohibited Content”</strong> includes, without limitation, Content that, in the sole
      discretion of OpenLikes: (1) is offensive or promotes racism, bigotry, hatred or physical harm
      of any kind; (2) harasses or advocates harassment of another person; (3) involves the
      transmission of “junk mail,” “chain letters,” unsolicited mass mailing or “spamming”; (4) is
      false, misleading, libelous, defamatory or promotes, endorses or furthers illegal activities;
      (5) infringes the Intellectual Property Rights of another person; (6) is involved in the
      exploitation of persons under the age of 18 in a sexual or violent manner, or solicits
      personal information from anyone under 13; (7) contains viruses, Trojan horses, worms, time
      bombs, cancelbots, corrupted files, spyware, malware or other harmful content or code; or (8)
      otherwise violates applicable law, these Terms of Service or potentially creates liability for
      OpenLikes. Any accounts posting, hosting or recommending Prohibited Content are subject to
      termination. We reserve the right to remove Content or links to Content that we determine in
      our sole discretion is prohibited under these rules. We also reserve the right to remove
      Content that we determine in our sole discretion is primarily intended to promote a product or
      service. We have the right (but not the obligation) to investigate or review any Content and
      delete (or modify) any Content that in our sole judgment violates these Terms of Service or
      which is Prohibited Content, or may otherwise violate the rights, harm or threaten the safety
      of any User or any other person, or create potential liability for us or any User.
    </p>
    <hr />
    <h5>Copyright Policy</h5>
    <p>
      OpenLikes respects the Intellectual Property Rights of creators. If you believe that your work
      has been displayed, posted, copied or included within the Services in a way that constitutes
      copyright infringement, please first check to see if the work originated from a third party
      service. You may notify such third party service directly about alleged copyright violations.
      For all other copyright notifications, please provide our Copyright Agent with the following
      information: (1) a digital or physical signature of the person authorized to act on behalf of
      the owner of the copyright interest; (2) a description of the copyrighted work that you claim
      has been infringed; (3) a description of where the material that you claim is infringing is
      located on the Services; (4) your address, telephone number and e-mail address; (5) a written
      statement by you that you have a good faith belief that the disputed use is not authorized by
      the copyright owner, its agent, or the law; and (6) a statement by you, made under penalty of
      perjury, that the above information in your notice is accurate and that you are the copyright
      owner or authorized to act on the copyright owner’s behalf. Contact information for our
      Copyright Agent for notice of claims of copyright infringement is as follows:
    </p>
    <p>
      OpenLikes Tech, Inc.
      <br />Attn: Copyright Agent <br />660 4th Street #558 <br />San Francisco, CA 94107
      <br />E-mail:
      <a href="mailto:openlikes@expa.com">openlikes@expa.com</a>
    </p>
    <p>
      We reserve the right to remove Content alleged to be infringing in our sole discretion and
      without notice. It is our policy to terminate the account of any OpenLikes User who repeatedly
      is accused of infringing copyrights.
    </p>
    <hr />
    <h5>Termination &amp; Your Breach of These Terms of Service</h5>
    <p>
      Any breach by you of these Terms of Service may result in, among other things, termination or
      suspension of your rights to use the Services and deactivation of your OpenLikes account.
      OpenLikes reserves the right to modify, suspend, limit or terminate the Services or your
      access to the Services for any reason, with or without notice, at any time, and without any
      liability or obligation to you. If we terminate your access to the Service or you deactivate
      your account, your Content and your posts, photos, comments, connections, followers, and all
      other data will no longer be accessible through your account, but your Content or other data
      may continue to exist within and/or be displayed via the Service. In addition, you acknowledge
      and agree that OpenLikes may store Content removed from the Services, including, without
      limitation, in order to comply with certain legal obligations.
    </p>
    <hr />
    <h5>Legal Obligations</h5>
    <p>
      In order to cooperate with legitimate government or regulator requests, subpoenas or court
      orders, to protect our systems and Users, to protect the property and rights of OpenLikes or a
      third party, to protect the safety of the public or any person, or to prevent or stop activity
      we may consider to be, or to pose a risk of being, illegal, unethical or legally actionable,
      you agree that we may access and disclose any information and Content we consider necessary or
      appropriate, including, without limitation, OpenLikes account information (i.e. name, e-mail
      address, etc.), IP address, traffic information, usage history and Content.
    </p>
    <hr />
    <h5>Availability</h5>
    <p>
      While OpenLikes strives to make the Services as accessible and functional as possible, from
      time to time the Services may be interrupted, including, due to scheduled maintenance or
      upgrades, for emergency repairs, or due to failure of equipment, servers or software, and/or
      due to issues with telecommunications services or providers. As a result, the Services and any
      Content may not always be accessible or reliable. You acknowledge and agree that the Services
      and any Content may not always be accessible, that you are not relying on the Services to
      store or backup your Content, and that OpenLikes will not be liable to you for any
      modification, suspension, or discontinuation of the Services, or the loss of any Content. You
      also acknowledge that the Internet may be subject to breaches of security and that the
      submission of Content or other information may not be secure.
    </p>
    <hr />
    <h5>Disclaimer of Warranties</h5>
    <p>
      THE SERVICES AND THE MIX CONTENT, IS PROVIDED ON AN “AS IS”, “AS AVAILABLE” BASIS. TO THE
      FULLEST EXTENT PERMISSIBLE BY LAW, NEITHER MIX NOR ANY OF ITS EMPLOYEES, MANAGERS, OFFICERS OR
      AGENTS (COLLECTIVELY, THE “MIX PARTIES”) MAKE ANY REPRESENTATIONS OR WARRANTIES OF ANY KIND
      WHATSOEVER, EXPRESS OR IMPLIED, AS TO: (A) THE SERVICE; (B) THE MIX CONTENT; (C) USER CONTENT;
      (D) THIRD PARTY CONTENT; OR (E) SECURITY ASSOCIATED WITH THE TRANSMISSION OF INFORMATION TO
      MIX OR VIA THE SERVICES. THE MIX PARTIES HEREBY DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
      INCLUDING, BUT NOT LIMITED TO, THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
      PURPOSE AND NON-INFRINGEMENT. THE MIX PARTIES MAKE NO REPRESENTATIONS OR WARRANTIES THAT THE
      SERVICES WILL BE ERROR-FREE OR UNINTERRUPTED; THAT DEFECTS WILL BE CORRECTED; OR THAT THE
      SERVICES OR ANY EQUIPMENT OR SOFTWARE THAT MAKE THE SERVICES AVAILABLE ARE FREE FROM ANY
      HARMFUL COMPONENTS, INCLUDING, WITHOUT LIMITATION, VIRUSES. THE MIX PARTIES MAKE NO
      REPRESENTATIONS OR WARRANTIES THAT ANY INFORMATION ON THE SERVICE IS ACCURATE, COMPLETE, OR
      USEFUL. YOU ACKNOWLEDGE AND AGREE THAT YOU ARE USING OR ACCESSING THE SERVICE AT YOUR SOLE
      RISK. THE MIX PARTIES MAKE NO REPRESENTATIONS OR WARRANTIES THAT YOUR USE OF THE SERVICE IS
      LAWFUL IN ANY PARTICULAR JURISDICTION, AND THE MIX PARTIES SPECIFICALLY DISCLAIM SUCH
      WARRANTIES. SOME JURISDICTIONS LIMIT OR DO NOT ALLOW THE DISCLAIMER OF IMPLIED OR OTHER
      WARRANTIES SO THE ABOVE DISCLAIMER MAY NOT APPLY TO YOU. THE MIX PARTIES DO NOT ENDORSE ANY
      USER CONTENT OR THIRD PARTY CONTENT AND SPECIFICALLY DISCLAIM ANY RESPONSIBILITY OR LIABILITY
      TO ANY PERSON OR ENTITY FOR ANY LOSS, DAMAGE (WHETHER ACTUAL, CONSEQUENTIAL, PUNITIVE OR
      OTHERWISE), INJURY, CLAIM, LIABILITY OR OTHER CAUSE OF ANY KIND OR CHARACTER BASED UPON OR
      RESULTING FROM ANY CONTENT.
    </p>
    <hr />
    <h5>Limitation of Liability</h5>
    <p>
      THE MIX PARTIES WILL NOT BE LIABLE TO YOU FOR, AND YOU AGREE TO HOLD THE MIX PARTIES HARMLESS
      FROM, ANY LOSS OR DAMAGES OF ANY KIND (INCLUDING, WITHOUT LIMITATION, FOR ANY DIRECT,
      INDIRECT, ECONOMIC, EXEMPLARY, SPECIAL, PUNITIVE, INCIDENTAL OR CONSEQUENTIAL LOSSES OR
      DAMAGES) THAT RESULT FROM OR ARE DIRECTLY OR INDIRECTLY RELATED TO: (1) THE SERVICE; (2) THE
      MIX CONTENT; (3) USER CONTENT; (4) THIRD-PARTY CONTENT; (5) YOUR USE OR ACCESS OF, OR
      INABILITY TO USE OR ACCESS, THE SERVICE OR THE PERFORMANCE OF THE SERVICE; (6) ANY ACTION
      TAKEN IN CONNECTION WITH AN INVESTIGATION BY THE MIX PARTIES OR LAW ENFORCEMENT AUTHORITIES
      REGARDING YOUR OR ANY OTHER PARTY’S USE OF THE SERVICE; (7) ANY ACTION TAKEN IN CONNECTION
      WITH RESPECT TO THE INTELLECTUAL PROPERTY RIGHTS OR PROPRIETARY RIGHTS OF OTHERS; (8) ANY
      ERRORS OR OMISSIONS IN THE SERVICE’S OPERATION; OR (9) ANY DAMAGE TO ANY USER’S COMPUTER,
      MOBILE DEVICE, OR OTHER EQUIPMENT, EVEN IF FORESEEABLE AND REGARDLESS OF WHETHER THE MIX
      PARTIES HAVE BEEN ADVISED OF OR SHOULD HAVE KNOWN OF THE POSSIBILITY OF SUCH DAMAGES, WHETHER
      IN AN ACTION OF CONTRACT, NEGLIGENCE, STRICT LIABILITY OR TORT. IN NO EVENT WILL THE MIX
      PARTIES BE LIABLE TO YOU OR ANYONE ELSE FOR LOSS, DAMAGE OR INJURY, INCLUDING, WITHOUT
      LIMITATION, DEATH OR PERSONAL INJURY. SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF
      INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATION OR EXCLUSION MAY NOT APPLY TO
      YOU. IN NO EVENT WILL THE MIX PARTIES’ TOTAL LIABILITY TO YOU FOR ALL DAMAGES, LOSSES OR
      CAUSES OR ACTION EXCEED ONE HUNDRED DOLLARS ($100.00). YOU AGREE THAT IN THE EVENT YOU INCUR
      ANY DAMAGES, LOSSES OR INJURIES THAT ARISE OUT OF MIX’S ACTS, OMISSIONS OR NEGLIGENCE, ANY
      SUCH DAMAGES, LOSSES OR INJURIES ARE NOT IRREPARABLE OR SUFFICIENT TO ENTITLE YOU TO
      INJUNCTIVE OR EQUITABLE RELIEF OR TO PREVENT ANY EXPLOITATION OF ANY WEBSITE, SERVICE,
      PROPERTY, PRODUCT OR OTHER CONTENT OWNED OR CONTROLLED BY THE MIX PARTIES, AND YOU WILL HAVE
      NO RIGHTS TO ENJOIN OR RESTRAIN THE DEVELOPMENT, PRODUCTION, DISTRIBUTION, ADVERTISING,
      EXHIBITION OR EXPLOITATION OF ANY WEBSITE, PROPERTY, PRODUCT, SERVICE, OR OTHER CONTENT OWNED
      OR CONTROLLED BY THE MIX PARTIES.
    </p>
    <hr />
    <h5>Release</h5>
    <p>
      You hereby release us, our officers, employees, investors, agents and successors from claims,
      demands, any and all losses, damages, rights, claims, and actions of any kind including,
      without limitation, personal injuries, death and property damage, that is either directly or
      indirectly related to or arises from (1) any interaction you have with other Users, or (2)
      your participation in any of our offline events. If you are a California resident, you hereby
      waive California Civil Code Section 1542, which states: “A general release does not extend to
      claims which the creditor does not know or suspect to exist in his favor at the time of
      executing the release, which, if known by him must have materially affected his settlement
      with the debtor.”
    </p>
    <hr />
    <h5>Indemnity</h5>
    <p>
      You agree to defend, indemnify and hold the OpenLikes Parties harmless from and against any
      claims, liabilities, damages, losses and expenses, including, without limitation, reasonable
      legal and accounting fees, arising out of or in any way connected with your Content, your
      access to or use of the Services, your violation of any law or regulation, and your violation
      of these Terms of Service. You agree to cooperate in the defense of any claim to the full
      extent requested by OpenLikes. OpenLikes reserves the right to assume the exclusive defense
      and control of any matter subject to indemnification by you, and you will not in any event
      settle any claim without the prior written consent of OpenLikes.
    </p>
    <hr />
    <h5>Other Users</h5>
    <p>
      Your interaction with other Users on the Services or with advertisers or other third parties,
      including any payment or delivery of goods or services, and any other terms, conditions,
      warranties or representations associated with such dealings, are solely between you and the
      other User, advertiser or third party. You agree that OpenLikes will not be responsible for
      any loss or damage incurred as the result of any such dealings or with respect to any third
      party’s use or disclosure of your personal information that you make available to them. If
      there is a dispute between you and any third party (including any User), we are under no
      obligation to become involved and you agree not to involve us unless we request otherwise in
      writing; however, we reserve the right, but have no obligation, to monitor disputes between
      you and other Users, Advertisers or other third parties.
    </p>
    <hr />
    <h5>Third Party Services &amp; Partner Communities</h5>
    <p>
      The Services may contain links to or integrations with third party websites, applications
      and/or services (
      <strong>“Third Party Services”</strong>) (1) displayed as part of the Services, (2) placed by
      us or third parties as a service to those interested in this information; or (3) posted by
      other Users. We may also place links or enable sharing or integration with Partner Communities
      or other Users. You use all such links and integrations at your own risk. To the extent such
      links or integrations are provided by us, you understand that they do not imply our
      endorsement, adoption or sponsorship of or affiliation with such Third Party Service or
      Partner Community. We accept no responsibility for reviewing changes or updates to, or the
      quality, content, policies, nature or reliability of Third Party Services or Partner
      Communities, or links or integrations from third parties back to the Services. You should
      review applicable terms and policies, including privacy and data gathering practices, for any
      Third Party Services or Partner Communities and should make whatever investigation you feel
      necessary or appropriate and choose appropriate settings for sharing your personal information
      before proceeding with any interaction with a Third Party Service or Partner Community.
    </p>
    <hr />
    <h5>Comments</h5>
    <p>
      If you have comments on the OpenLikes Services or ideas on how we might improve, please
      contact us
      <a href="mailto:openlikes@expa.com">here</a>. You acknowledge and agree that if you submit any
      comments or ideas to us, you hereby grant to us a non-exclusive, worldwide, perpetual,
      irrevocable, fully-paid, royalty-free, sub-licensable and transferable license under any and
      all Intellectual Property Rights that you own or control to use, copy, modify, create
      derivative works based upon and otherwise exploit the comments and ideas for any purpose. The
      licenses granted here shall survive the termination or expiration of these Terms of Service.
    </p>
    <hr />
    <h5>Initial Resolution of Questions or Disputes</h5>
    <p>
      If you have any questions or concerns about the Services or these Terms of Services, we
      strongly encourage you to first contact us directly
      <a href="mailto:openlikes@expa.com">here</a>.
    </p>
    <hr />
    <h5>Arbitration &amp; Waiver of Class Action</h5>
    <p>
      For any claim (excluding claims for injunctive or other equitable relief) where the total
      amount of the award sought is less than $10,000, either party may elect to resolve the dispute
      in a cost effective manner through binding non-appearance-based arbitration. In the event a
      party elects arbitration, they shall initiate such arbitration through an established
      alternative dispute resolution (“ADR”) provider mutually agreed upon by the parties. The ADR
      provider and the parties must comply with the following rules: (1) the arbitration shall be
      conducted by telephone, online and/or be solely based on written submissions, the specific
      manner shall be chosen by the party initiating the arbitration; (2) the arbitration shall not
      involve any personal appearance by the parties or witnesses unless otherwise mutually agreed
      by the parties; and (3) any judgment on the award rendered by the arbitrator may be entered in
      any court of competent jurisdiction. Any dispute resolution proceedings, whether in
      arbitration or court, will be conducted only on an individual basis and not in a class or
      representative action or as a named or unnamed member in a class, consolidated, representative
      or private attorney general legal action.
    </p>
    <hr />
    <h5>Law &amp; Jurisdiction</h5>
    <p>
      These Terms of Service shall be governed in all respects by the laws of the State of
      California without giving effect to any conflicts of laws principles that require the
      application of the law of a different jurisdiction. You agree that any claim or dispute you
      may have against OpenLikes must be resolved by a court located in San Francisco County,
      California, except as otherwise agreed by the parties or as in the arbitration option. You
      agree to submit to the personal jurisdiction of the courts located within San Francisco
      County, California for the purpose of litigating all such claims or disputes.
    </p>
    <hr />
    <h5>Other Provisions</h5>
    <p>
      These Terms of Service constitute the entire agreement between you and OpenLikes with respect
      your use of the Services and supersede and replace all prior or contemporaneous communications
      or agreements, written or oral, including any prior versions of the Terms of Service. These
      Terms of Service do not confer any third-party beneficiary rights on any person or entity. If
      for any reason a court of competent jurisdiction finds any provision or portion of these Terms
      of Service to be unenforceable, the remainder of the Terms of Service will continue in full
      force and effect. Any waiver of any provision of these Terms of Service will be effective only
      if in writing and signed by OpenLikes. The provisions of these Terms of Service that by their
      nature are continuing will survive any termination.
    </p>
    <br />
  </div>
</template>

<script>
export default {
  props: {},

  data() {
    return {}
  },

  computed: {},

  watch: {},

  methods: {},

  components: {},
}

/* Computed ---------------------------------------------------- */
/* Watch ------------------------------------------------------- */
/* Methods ----------------------------------------------------- */
</script>
